import { getAccessToken } from "@auth0/nextjs-auth0/client";
import {
  AccountClientFetch,
  ArtClientPublicFetch,
  CartClient,
  EventClientPublicFetch,
  GoogleClientFetch,
  OrderClientFetch,
  OrderClientPublicFetch,
  PaymentClientFetch,
  PricingClientFetch,
  ProductClientPublicFetch,
} from "@sticker-giant/sg-frontend-common";
import { decodeJwt } from "jose";
import { create } from "zustand";
import { requireEnv } from "@/lib/env";
import { PricingClientPublic } from "@/services/PricingClientPublic";
import { type AuthTokenClaims, useAuthClaimsStore } from "./useAuthClaimsStore";

interface ServicesStore {
  accountClientFetch: AccountClientFetch | null;
  artClientPublic: ArtClientPublicFetch | null;
  authResolved: boolean;
  cartClient: CartClient | null;
  eventClientPublicFetch: EventClientPublicFetch | null;
  googleClient: GoogleClientFetch | null;
  initClients: (getToken?: () => Promise<string>) => Promise<void>;
  orderClient: OrderClientFetch | null;
  orderClientPublic: OrderClientPublicFetch | null;
  paymentClient: PaymentClientFetch | null;
  pricingClientFetch: PricingClientFetch | null;
  pricingClientPublic: PricingClientPublic | null;
  productClientPublic: ProductClientPublicFetch | null;
  setArtClientPublic: (client: ArtClientPublicFetch) => void;
  setCartClient: (client: CartClient) => void;
  setGoogleClient: (client: GoogleClientFetch) => void;
  setOrderClient: (client: OrderClientFetch) => void;
  setOrderClientPublic: (client: OrderClientPublicFetch) => void;
  setPaymentClient: (client: PaymentClientFetch) => void;
  setPricingClientPublic: (client: PricingClientPublic) => void;
  setProductClientPublic: (client: ProductClientPublicFetch) => void;
  setEventClientPublicFetch: (client: EventClientPublicFetch) => void;
  setTokenProvider: (provider: () => Promise<string>) => void;
  tokenProvider?: () => Promise<string>;
}

const safelyGetAuth0Token = async (): Promise<string | null> => {
  try {
    return await getAccessToken();
  } catch {
    return null;
  }
};

const useServicesStore = create<ServicesStore>(set => {
  const accountServiceUrl = requireEnv(process.env.NEXT_PUBLIC_ACCOUNT_SERVICE_URL, "NEXT_PUBLIC_ACCOUNT_SERVICE_URL");
  const artServiceUrl = requireEnv(process.env.NEXT_PUBLIC_ART_SERVICE_URL, "NEXT_PUBLIC_ART_SERVICE_URL");
  const cartServiceUrl = requireEnv(process.env.NEXT_PUBLIC_CART_SERVICE_URL, "NEXT_PUBLIC_CART_SERVICE_URL");
  const eventServiceUrl = requireEnv(process.env.NEXT_PUBLIC_EVENT_SERVICE_URL, "NEXT_PUBLIC_EVENT_SERVICE_URL");
  const googleApiKey = requireEnv(process.env.NEXT_PUBLIC_GOOGLE_API_KEY, "NEXT_PUBLIC_GOOGLE_API_KEY");
  const googleVerifyAddressUrl = requireEnv(
    process.env.NEXT_PUBLIC_GOOGLE_VERIFY_ADDRESS_URL,
    "NEXT_PUBLIC_GOOGLE_VERIFY_ADDRESS_URL",
  );
  const orderServiceUrl = requireEnv(process.env.NEXT_PUBLIC_ORDER_SERVICE_URL, "NEXT_PUBLIC_ORDER_SERVICE_URL");
  const paymentServiceUrl = requireEnv(process.env.NEXT_PUBLIC_PAYMENT_SERVICE_URL, "NEXT_PUBLIC_PAYMENT_SERVICE_URL");
  const pricingServiceUrl = requireEnv(process.env.NEXT_PUBLIC_PRICING_SERVICE_URL, "NEXT_PUBLIC_PRICING_SERVICE_URL");
  const productServiceUrl = requireEnv(process.env.NEXT_PUBLIC_PRODUCT_SERVICE_URL, "NEXT_PUBLIC_PRODUCT_SERVICE_URL");

  const initClients = async () => {
    if (typeof window === "undefined") return;

    set({
      cartClient: new CartClient("", cartServiceUrl),
      googleClient: new GoogleClientFetch(googleApiKey, googleVerifyAddressUrl),
    });

    try {
      const apiToken = await safelyGetAuth0Token();
      if (!apiToken) return;

      const claims = decodeJwt(apiToken) as AuthTokenClaims;
      useAuthClaimsStore.getState().setClaims(claims);

      set({
        cartClient: new CartClient(apiToken, cartServiceUrl),
        pricingClientFetch: new PricingClientFetch(apiToken, pricingServiceUrl),
        accountClientFetch: new AccountClientFetch(apiToken, accountServiceUrl),
        orderClient: new OrderClientFetch(apiToken, orderServiceUrl),
        paymentClient: new PaymentClientFetch(apiToken, paymentServiceUrl),
      });
    } finally {
      set({ authResolved: true });
    }
  };

  // Defer so initClients' set() calls land AFTER this factory's return object is
  // applied as the initial state — otherwise zustand wipes the lazy clients back to null.
  queueMicrotask(() => {
    void initClients();
  });

  return {
    authResolved: false,
    artClientPublic: new ArtClientPublicFetch(artServiceUrl),
    setArtClientPublic: (client: ArtClientPublicFetch) => set(() => ({ artClientPublic: client })),
    eventClientPublicFetch: new EventClientPublicFetch(eventServiceUrl),
    orderClientPublic: new OrderClientPublicFetch(orderServiceUrl),
    setOrderClientPublic: (client: OrderClientPublicFetch) => set(() => ({ orderClientPublic: client })),
    cartClient: null,
    pricingClientFetch: null,
    orderClient: null,
    setOrderClient: (client: OrderClientFetch) => set(() => ({ orderClient: client })),
    paymentClient: null,
    setPaymentClient: (client: PaymentClientFetch) => set(() => ({ paymentClient: client })),
    productClientPublic: new ProductClientPublicFetch(productServiceUrl),
    setProductClientPublic: (client: ProductClientPublicFetch) => set(() => ({ productClientPublic: client })),
    setCartClient: (client: CartClient) => set(() => ({ cartClient: client })),
    accountClientFetch: null,
    googleClient: null,
    setGoogleClient: (client: GoogleClientFetch) => set(() => ({ googleClient: client })),
    pricingClientPublic: new PricingClientPublic(pricingServiceUrl),
    setPricingClientPublic: (client: PricingClientPublic) => set(() => ({ pricingClientPublic: client })),
    setEventClientPublicFetch: (client: EventClientPublicFetch) => set(() => ({ eventClientPublicFetch: client })),
    setTokenProvider: (provider: () => Promise<string>) => set(() => ({ tokenProvider: provider })),
    tokenProvider: undefined,
    initClients,
  };
});

export { useServicesStore };
